import { ColumnDef } from '@tanstack/react-table'

/**
 * Content-type → minimum-width rules for data-table columns.
 *
 * This is the "up-front rule" layer: every column declares what *kind* of
 * content it holds (via `meta.columnType`), and the table enforces a sensible
 * minimum width for that kind so a user can never resize (or a layout can never
 * squeeze) a column narrower than its content can legibly render.
 *
 * The rules live here, decoupled from any one table implementation, so they can
 * be wired into both the original DataTable and the newer ClientDataTable (v2)
 * without duplicating the policy.
 */

export type ColumnContentType =
  /** First column hosting the bulk-select checkbox + "Selected (N)" dropdown +
   *  optional bulk Actions button. Its real floor is *measured* at runtime from
   *  the rendered control (see useMeasuredFloor); this registry value is only a
   *  pre-measurement fallback. */
  | 'select-actions'
  | 'text'
  | 'company-name'
  | 'email'
  | 'url'
  | 'currency'
  | 'number'
  | 'count'
  | 'percent'
  | 'date'
  | 'status'
  /** Trailing row-actions column (kebab menu, icon buttons). */
  | 'actions'

/**
 * Minimum width in px per content type. Tuned to the smallest width at which
 * the typical formatted value for that type renders without truncation in the
 * table's 14px Inter body font.
 */
export const COLUMN_MIN_WIDTHS: Record<ColumnContentType, number> = {
  'select-actions': 232, // fallback only; overridden by the measured floor
  text: 120,
  'company-name': 180,
  email: 200,
  url: 200,
  currency: 112,
  number: 96,
  count: 80,
  percent: 88,
  date: 128,
  status: 112,
  actions: 64,
}

/**
 * Floor used when a column declares no `columnType` and no explicit override.
 * Set to a readable default so untyped columns never collapse to an unusably
 * narrow width; columns that need more (emails, urls) or less (counts) should
 * declare a `columnType` to tune their floor precisely.
 */
export const DEFAULT_COLUMN_MIN_WIDTH = 120

/**
 * Sizing-related fields a column may declare on its TanStack `meta`. Columns set
 * these in their `columnHelper.accessor(..., { meta: { columnType: 'currency' } })`
 * definitions. Kept as a standalone interface (rather than a global meta
 * augmentation) so the module is self-contained and portable.
 */
export interface ColumnSizingMeta {
  /** Content type, drives the registry-based minimum width. */
  columnType?: ColumnContentType
  /** Hard override of the minimum width in px. Wins over `columnType`. */
  minWidth?: number
  /** Marks the display-first column (already used by ColumnsStore for ordering). */
  isFirstColumn?: boolean
  /** Marks the trailing column — in practice the fixed-width Actions/kebab
   *  column, which is pinned (never user-resizable). */
  isLastColumn?: boolean
  /** Opt a single column out of user resizing (escape hatch beyond columnType). */
  disableResizing?: boolean
}

/**
 * Content types that are fixed-width by nature and must never be user-resizable.
 * The trailing Actions column (icon/kebab buttons) is the canonical case: its
 * width is dictated by its controls, not by content the user would want wider.
 */
const NON_RESIZABLE_TYPES: ReadonlySet<ColumnContentType> = new Set(['actions'])

/**
 * Whether a column may be drag-resized. Actions columns (and anything that opts
 * out via `meta.disableResizing`) are pinned to their declared width.
 */
export function isColumnResizable(columnDef: ColumnDef<any, any>): boolean {
  const meta = readSizingMeta(columnDef)
  if (meta?.disableResizing) return false
  // The trailing Actions column is fixed-width by rule, whether it declares
  // columnType 'actions' or just isLastColumn.
  if (meta?.isLastColumn) return false
  if (meta?.columnType && NON_RESIZABLE_TYPES.has(meta.columnType)) return false
  return true
}

function readSizingMeta(
  columnDef: ColumnDef<any, any>
): ColumnSizingMeta | undefined {
  return columnDef.meta as ColumnSizingMeta | undefined
}

/**
 * Resolve a column definition's id the same way TanStack does: an explicit `id`,
 * otherwise the `accessorKey` (for accessor columns). Needed to match a def
 * against a resolved column id (e.g. from columnOrder) when deciding which
 * column is displayed first.
 */
export function resolveColumnDefId(
  columnDef: ColumnDef<any, any>
): string | undefined {
  if (columnDef.id) return columnDef.id
  const accessorKey = (columnDef as { accessorKey?: unknown }).accessorKey
  return typeof accessorKey === 'string' ? accessorKey : undefined
}

/**
 * Resolve the enforced minimum width (px) for a single column definition.
 *
 * Precedence:
 *   1. explicit `meta.minWidth`
 *   2. the measured actions-button floor, for the first column when row
 *      selection is enabled (so the bulk-select control never gets clipped)
 *   3. `COLUMN_MIN_WIDTHS[meta.columnType]`
 *   4. DEFAULT_COLUMN_MIN_WIDTH
 */
export function resolveColumnMinWidth(
  columnDef: ColumnDef<any, any>,
  opts: {
    /** True for the column rendered first (hosts the bulk-select control). */
    isFirstColumn?: boolean
    /** Measured natural width (px) of the rendered bulk-select + actions header. */
    measuredSelectFloor?: number
  } = {}
): number {
  const meta = readSizingMeta(columnDef)
  const { isFirstColumn, measuredSelectFloor } = opts

  if (typeof meta?.minWidth === 'number') {
    // An explicit override still cannot be narrower than the live actions
    // control on the first column — clamp up so the rule can't be opted out of.
    if (isFirstColumn && measuredSelectFloor) {
      return Math.max(meta.minWidth, measuredSelectFloor)
    }
    return meta.minWidth
  }

  if (isFirstColumn) {
    // The first column must always fit the bulk-select + Actions control, even
    // if that exceeds its declared default size — so it is NOT capped below.
    const floor = Math.max(
      measuredSelectFloor ?? 0,
      COLUMN_MIN_WIDTHS['select-actions']
    )
    if (meta?.columnType && meta.columnType !== 'select-actions') {
      return Math.max(floor, COLUMN_MIN_WIDTHS[meta.columnType])
    }
    return floor
  }

  // For every other column, the minimum should never push it wider than the
  // default width its author intended (`size`). This keeps deliberately-narrow
  // columns (icon/actions columns, counts) from being inflated by the generic
  // floor while still preventing readable columns from collapsing.
  const declaredSize =
    typeof columnDef.size === 'number' ? columnDef.size : undefined
  const base = meta?.columnType
    ? COLUMN_MIN_WIDTHS[meta.columnType]
    : DEFAULT_COLUMN_MIN_WIDTH

  return declaredSize !== undefined ? Math.min(base, declaredSize) : base
}

/**
 * Return a copy of `columns` with `minSize` injected on every definition per the
 * rules above. TanStack's `column.getSize()` clamps to `minSize`, so this both
 * (a) prevents drag-resize below the floor and (b) keeps a too-small persisted
 * width from ever rendering narrower than the floor.
 *
 * `firstColumnId` identifies the column that hosts the bulk-select control; it
 * receives the measured floor. If omitted, the first entry in `columns` is used.
 */
export function applyColumnMinWidths<T>(
  columns: ColumnDef<T, any>[],
  opts: {
    firstColumnId?: string
    measuredSelectFloor?: number
    /** When false, the first-column actions floor is not applied. */
    rowSelectionEnabled?: boolean
  } = {}
): ColumnDef<T, any>[] {
  const { firstColumnId, measuredSelectFloor, rowSelectionEnabled } = opts

  const resolvedFirstId =
    firstColumnId ??
    resolveColumnDefId(
      columns.find(
        (c) => (c.meta as ColumnSizingMeta | undefined)?.isFirstColumn
      ) ?? columns[0]
    )

  return columns.map((columnDef) => {
    const isFirstColumn =
      !!rowSelectionEnabled && resolveColumnDefId(columnDef) === resolvedFirstId
    const minSize = resolveColumnMinWidth(columnDef, {
      isFirstColumn,
      measuredSelectFloor,
    })
    return {
      ...columnDef,
      minSize,
      // Pin fixed-width columns (e.g. Actions) so they can't be dragged.
      enableResizing: isColumnResizable(columnDef),
    }
  })
}
